import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Code2, Globe, MessageCircle, MessagesSquare, Plus, Sparkles } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { FormField } from '@/components/FormField';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useAuth } from '@/hooks/useAuth';
import { aiAgentsService, AiAgent } from '@/services/ai-agents.service';
import { useBrainOptions } from './agentShared';

export default function AiAgentsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'company_admin';
  const [agents, setAgents] = useState<AiAgent[] | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    aiAgentsService.list().then(setAgents).catch(() => setAgents([]));
  }, []);

  return (
    <>
      <Topbar
        title="AI Agents"
        breadcrumb="AI Agents / Agents"
        right={isAdmin && agents?.length ? <Button iconLeft={<Plus size={14} />} onClick={() => setCreating(true)}>New agent</Button> : undefined}
      />
      <div className="w-full px-4 py-6 sm:px-7 sm:py-7">
        {agents === null ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3].map((n) => <div key={n} className="skeleton h-44 rounded-2xl" />)}
          </div>
        ) : agents.length === 0 ? (
          <Intro isAdmin={isAdmin} onCreate={() => setCreating(true)} />
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {agents.map((a) => <AgentCard key={a.agentUid} agent={a} onOpen={() => navigate(`/ai-agents/${a.agentUid}`)} />)}
          </div>
        )}
      </div>
      {creating && (
        <CreateAgentModal
          onClose={() => setCreating(false)}
          onCreated={(a) => { setCreating(false); navigate(`/ai-agents/${a.agentUid}?tab=website`); }}
        />
      )}
    </>
  );
}

function Intro({ isAdmin, onCreate }: { isAdmin: boolean; onCreate: () => void }) {
  const points = [
    { icon: <Globe size={16} />, title: 'Website chat', body: 'Paste one line on your site. Visitors chat with the same AI that answers your WhatsApp.' },
    { icon: <Code2 size={16} />, title: 'Chat API', body: 'Put the agent inside your own Android, iOS or web app with an API key.' },
    { icon: <MessagesSquare size={16} />, title: 'Your team can join', body: 'Every chat lands in Conversations. Take over any time — the AI steps back.' },
  ];
  return (
    <div className="mx-auto flex max-w-3xl flex-col items-center py-10 text-center sm:py-16">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gold-gradient text-on-primary shadow-elevated">
        <Bot size={30} />
      </div>
      <h2 className="font-display text-2xl font-semibold text-text-primary">One AI, everywhere your customers are</h2>
      <p className="mt-2 max-w-xl text-sm text-text-muted">
        An AI agent answers with your business profile, catalog and WhatsApp AI settings — so it says the same thing on your website and in your app as it does on WhatsApp. Chats with a phone or email become leads.
      </p>
      <div className="mt-8 grid w-full grid-cols-1 gap-3 text-left sm:grid-cols-3">
        {points.map((p) => (
          <div key={p.title} className="rounded-2xl border border-border bg-surface p-4">
            <span className="mb-2 flex h-8 w-8 items-center justify-center rounded-lg bg-primary-soft text-primary">{p.icon}</span>
            <div className="text-sm font-semibold text-text-primary">{p.title}</div>
            <p className="mt-1 text-xs leading-relaxed text-text-muted">{p.body}</p>
          </div>
        ))}
      </div>
      {isAdmin
        ? <Button className="mt-8" iconLeft={<Sparkles size={14} />} onClick={onCreate}>Create your first agent</Button>
        : <p className="mt-8 text-xs text-text-muted">Ask a workspace admin to create an agent.</p>}
    </div>
  );
}

function AgentCard({ agent, onOpen }: { agent: AiAgent; onOpen: () => void }) {
  const last = agent.stats?.lastMessageAt ? formatDistanceToNow(new Date(agent.stats.lastMessageAt), { addSuffix: true }) : 'no chats yet';
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex flex-col rounded-2xl border border-border bg-surface p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-elevated focus-visible:ring-4 focus-visible:ring-primary-soft"
      style={{ outline: 'none' }}
    >
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white" style={{ background: agent.webchat.color }}>
          <Bot size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-text-primary">{agent.name}</span>
            {!agent.isActive && <span className="rounded-full bg-surface-alt px-2 py-0.5 text-[10px] font-medium text-text-muted">Off</span>}
          </div>
          <p className="truncate text-xs text-text-muted">
            {agent.brain ? `Answers like ${agent.brain.businessName} · ${agent.brain.phone}` : 'Its own persona'}
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5">
        <ChannelChip on={agent.isActive && agent.webchat.enabled} icon={<Globe size={11} />} label="Website chat" />
        <ChannelChip on={agent.isActive && agent.apiEnabled} icon={<Code2 size={11} />} label="Chat API" />
        {!!agent.whatsappNumbers && <ChannelChip on={agent.isActive} icon={<MessageCircle size={11} />} label={`WhatsApp · ${agent.whatsappNumbers}`} />}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 border-t border-border pt-3 text-xs">
        <div><div className="font-semibold text-text-primary">{agent.stats?.conversations ?? 0}</div><div className="text-text-muted">Conversations</div></div>
        <div className="min-w-0"><div className="truncate font-semibold text-text-primary">{last}</div><div className="text-text-muted">Last message</div></div>
      </div>
    </button>
  );
}

function ChannelChip({ on, icon, label }: { on: boolean; icon: React.ReactNode; label: string }) {
  return (
    <span className={clsx('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium', on ? 'bg-success-soft text-success' : 'bg-surface-alt text-text-muted')}>
      {icon}{label}{on ? '' : ' · off'}
    </span>
  );
}

function CreateAgentModal({ onClose, onCreated }: { onClose: () => void; onCreated: (a: AiAgent) => void }) {
  const [name, setName] = useState('');
  const [brain, setBrain] = useState('');
  const [saving, setSaving] = useState(false);
  const { options, loading, firstAiAccount } = useBrainOptions();

  useEffect(() => { if (firstAiAccount) setBrain((b) => b || firstAiAccount); }, [firstAiAccount]);

  const save = async () => {
    if (!name.trim()) { toast.error('Give the agent a name'); return; }
    setSaving(true);
    try {
      onCreated(await aiAgentsService.create({ name: name.trim(), brainAccountUid: brain || undefined }));
      toast.success('Agent created — set up the website chat next');
    } catch (e: any) {
      if (!e?.response) toast.error('Could not create the agent');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="New AI agent">
      <div className="space-y-1">
        <FormField label="Name" required hint="Your team sees this; visitors see the chat title">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Website concierge" maxLength={80} autoFocus />
        </FormField>
        <FormField label="Answers like" subtitle="The agent uses this number's WhatsApp AI settings — persona, services, prices, the details it collects — plus your company profile and catalog.">
          <SearchableSelect options={options} value={brain} onChange={setBrain} disabled={loading} numbered={false} searchable={options.length > 6} aria-label="Answers like" />
        </FormField>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button loading={saving} onClick={save}>Create agent</Button>
        </div>
      </div>
    </Modal>
  );
}
